import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CompanyState } from '../../../../types/company';
import type { ResearchProgress } from '../../../../types/research';
import { researchTree } from '../../../../data/researchTree';

interface ResearchProgressSectionProps {
  companyState: CompanyState;
  onNavigate?: () => void;
}

export function ResearchProgressSection({ companyState, onNavigate }: ResearchProgressSectionProps) {
  const [currentView, setCurrentView] = useState<'active' | 'completed'>('active');
  
  if (!companyState) {
    return (
      <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
        <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 text-center font-bold text-sm text-theme-text-secondary border-b border-theme-border">
          Research
        </div>
        <div className="pt-10 p-3 h-full text-center text-theme-text-muted">
          Loading...
        </div>
      </div>
    );
  }

  // Get actual research from state
  const activeResearch = companyState.research?.activeResearch || [];
  const unlockedNodes = companyState.research?.unlockedNodes || [];
  const researchPoints = companyState.researchPoints || 0;
  
  // Get all nodes from research tree for name lookup
  const allNodes = researchTree.branches.flatMap(branch => branch.nodes);
  
  // Map active research to display format with proper names
  const researchProjects = activeResearch.map((research: ResearchProgress) => {
    const node = allNodes.find(n => n.id === research.nodeId);
    return {
      name: node?.name || research.nodeId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      progress: Math.round(research.progress || 0)
    };
  });

  // Map completed research to display format
  const completedResearch = unlockedNodes.map(nodeId => {
    const node = allNodes.find(n => n.id === nodeId);
    return {
      id: nodeId,
      name: node?.name || nodeId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      category: node?.category || 'unknown'
    };
  });

  const toggleView = () => {
    setCurrentView(currentView === 'active' ? 'completed' : 'active');
  };

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 flex items-center justify-between border-b border-theme-border">
        <button
          onClick={toggleView}
          className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
          title="Previous view"
        >
          <ChevronLeft className="w-3 h-3" />
        </button>
        <button
          onClick={onNavigate}
          className={`font-bold text-sm text-theme-text-secondary ${
            onNavigate ? 'hover:text-theme-info transition-colors cursor-pointer' : ''
          }`}
          disabled={!onNavigate}
        >
          Research
        </button>
        <button
          onClick={toggleView}
          className="p-1 hover:bg-theme-bg-tertiary rounded transition-colors"
          title="Next view"
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="pt-10 p-3 h-full overflow-y-auto scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {currentView === 'active' ? (
          // Active Research View
          <>
            {/* Always show RP available at the top */}
            <div className="bg-theme-info/10 rounded p-2 mb-2 border border-theme-info/30">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-theme-info">Research Points</span>
                <span className="font-bold text-xs text-theme-info">
                  {Math.floor(researchPoints)} RP
                </span>
              </div>
            </div>
            
            {/* Show active research or placeholder */}
            {researchProjects.length > 0 ? (
              researchProjects.map((project, index) => (
                <div key={`research-${index}-${project.name}`} className="bg-theme-bg-content/80 rounded p-2 mb-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium">{project.name}</span>
                    <span className="font-bold text-theme-success text-xs">
                      {project.progress}%
                    </span>
                  </div>
                  <div className="mt-1 bg-theme-bg-tertiary rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-theme-success h-full transition-all duration-300"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-theme-bg-content/60 rounded p-3 text-center">
                <span className="text-xs text-theme-text-muted">No Active Research</span>
              </div>
            )}
          </>
        ) : (
          // Completed Research View
          <>
            <div className="bg-theme-success/10 rounded p-2 mb-2 border border-theme-success/30">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-theme-success">Completed Research</span>
                <span className="font-bold text-xs text-theme-success">
                  {completedResearch.length} Total
                </span>
              </div>
            </div>
            
            {completedResearch.length > 0 ? (
              <div className="space-y-1">
                {completedResearch.map((research) => (
                  <div key={research.id} className="bg-theme-bg-content/80 rounded p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">{research.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        research.category === 'technology' ? 'bg-theme-info/20 text-theme-info' :
                        research.category === 'design' ? 'bg-theme-accent/20 text-theme-accent' :
                        research.category === 'business' ? 'bg-theme-success/20 text-theme-success' :
                        'bg-theme-bg-tertiary text-theme-text-secondary'
                      }`}>
                        {research.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-theme-bg-content/60 rounded p-3 text-center">
                <span className="text-xs text-theme-text-muted">No Completed Research</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}